import type { ReactNode } from 'react'
import clsx from 'clsx'

import { useDataStatus } from '../../lib/data'
import { SettingsNav, type SettingsPage } from '../connections/SettingsNav'
import { BuildBadge } from '../HeaderBand'

// ============================================================================
// THE SHELL THE SETTINGS SCREENS SHARE, plus the two small pieces more than one
// of them needs.
//
// NO MOCKUP SOURCE for any of these four screens — the handoff draws the
// settings nav's six rows and none of the screens behind them. So the geometry
// here is lifted from the two screens that DO exist (Connections and
// Appearance), not invented: the 236px nav, the 30px/20px header with the build
// badge pinned right, the `Section` rule, the 20px column gap.
//
// It is a component rather than four copies for one reason: the header padding
// is written once, so the six settings screens cannot slowly stop being the same
// screen.
// ============================================================================

export function SettingsScreen({
  active,
  title,
  /** One line under the title, or null. Never a restatement of the title. */
  subtitle,
  children,
}: {
  active: SettingsPage
  title: string
  subtitle?: string | null
  children: ReactNode
}) {
  // The nav's demo-mode footnote is the one thing the shell reads for itself:
  // README §12 — demo mode announces itself in the settings nav on EVERY
  // settings screen, not only on the one that happens to fetch the status.
  const dataStatus = useDataStatus()

  return (
    <div className="flex min-h-0 flex-1">
      <SettingsNav
        active={active}
        tone={null}
        toneReason={null}
        demoMode={dataStatus.data?.mode === 'demo'}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-y-auto">
        <header className="flex flex-none items-center gap-[14px] border-b border-line bg-surface px-[30px] pb-4 pt-5">
          <div className="flex flex-col gap-[5px]">
            <h1 className="text-[16px] font-semibold leading-none tracking-[-0.01em]">{title}</h1>
            {subtitle && (
              <p className="text-[11.5px] font-normal leading-none text-microcopy">{subtitle}</p>
            )}
          </div>
          <BuildBadge className="ml-auto" />
        </header>

        <div className="flex flex-col gap-5 px-[30px] pb-[30px] pt-[18px]">{children}</div>
      </div>
    </div>
  )
}

/**
 * A dotted sentence in a bordered row — the Appearance screen's "Browser
 * extensions" idiom, extracted because three of these four screens are mostly
 * made of it.
 *
 * THIS IS WHERE "NOT BUILT" GOES. A capability that does not exist gets one of
 * these rows saying so in words, and never a control that looks pressable. The
 * `tone` is neutral by default and only turns amber for something the owner
 * should not walk away believing is handled.
 */
export function NoteRow({
  children,
  tone = 'neutral',
}: {
  children: ReactNode
  tone?: 'neutral' | 'warning'
}) {
  const warning = tone === 'warning'
  return (
    <div
      className={clsx(
        'flex items-start gap-[11px] rounded-row border px-[15px] py-[13px]',
        warning ? 'border-warning-line bg-warning-fill' : 'border-line bg-surface',
      )}
    >
      <span
        className={clsx(
          'mt-[6px] size-[5px] flex-none rounded-full',
          warning ? 'bg-warning-dot' : 'bg-microcopy',
        )}
        aria-hidden
      />
      <p
        className={clsx(
          'min-w-0 flex-1 text-[11.5px] font-normal leading-[1.5] text-pretty',
          warning ? 'text-warning-2' : 'text-ink-3',
        )}
      >
        {children}
      </p>
    </div>
  )
}

/**
 * A label over a value, inside a card. The Connections screen's `StatPair`
 * without the ValueSwap animation: nothing on these screens arrives from a sync,
 * so a value that lifts and rises would be motion announcing news that is not
 * news.
 *
 * A null value renders an em dash and carries its reason in `title` — absent,
 * never a guessed zero and never a blank that reads as "nothing".
 */
export function FactRow({
  label,
  value,
  missingReason,
}: {
  label: string
  value: string | null
  /** Required whenever `value` can be null: what the app does not know, and why. */
  missingReason?: string
}) {
  return (
    <div className="flex items-baseline gap-[14px] px-[15px] py-[11px]">
      <span className="w-[132px] flex-none text-[9.5px] font-semibold uppercase leading-[1.5] tracking-[0.06em] text-microcopy">
        {label}
      </span>
      {value === null ? (
        <span title={missingReason} className="text-[11.5px] font-medium leading-[1.5] text-faint">
          —
        </span>
      ) : (
        <span className="min-w-0 flex-1 break-words text-[11.5px] font-medium leading-[1.5] text-ink-2">
          {value}
        </span>
      )}
    </div>
  )
}

/** The card `FactRow`s sit in: hairline-separated rows on one surface. */
export function FactCard({ children }: { children: ReactNode }) {
  return (
    <div className="flex flex-col divide-y divide-line rounded-card border border-line bg-surface shadow-card">
      {children}
    </div>
  )
}

/**
 * An endpoint, named inside a sentence. Monospace on the token's own font stack
 * so a path is visibly a path — the machine-readability rule means these get
 * quoted constantly on these screens, and prose that swallows `/api/whoami` into
 * the line makes it look like a phrase rather than a thing you can curl.
 */
export function ApiPath({ children }: { children: string }) {
  return (
    <span className="whitespace-nowrap rounded-[4px] bg-fill px-[5px] py-[1px] font-mono text-[10.5px] text-ink-2">
      {children}
    </span>
  )
}

/**
 * ONE OPTION IN A TWO-OR-MORE-WAY CHOICE — the Appearance screen's size tile,
 * verbatim in shape, because a second settings idiom for the same kind of control
 * is how a design system stops being one.
 *
 * A REAL RADIO INPUT, visually replaced. The tile has to be reachable with the
 * arrow keys and announce itself as a radio, and rebuilding that on a <button> is
 * how a settings screen loses its keyboard. `autoComplete="off"` for the same
 * reason Appearance gives: Chrome restores radio state across a reload and the
 * restored radio fires `change`, which would quietly rewrite the stored
 * preference to whatever was last checked in the tab.
 */
export function OptionTile({
  name,
  value,
  label,
  /** What picking it actually does. Never a restatement of the label. */
  detail,
  selected,
  onPick,
}: {
  name: string
  value: string
  label: string
  detail: string
  selected: boolean
  onPick: () => void
}) {
  return (
    <label
      className={clsx(
        'flex flex-1 cursor-pointer flex-col gap-[9px] rounded-tile border px-[15px] py-[13px]',
        'transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)]',
        // Keyboard focus only — the same `has-[:focus-visible]` narrowing Appearance uses, so a
        // mouse click does not stack the browser's outline on the tile's selected border.
        'has-[:focus-visible]:outline has-[:focus-visible]:outline-2 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-focus-ring',
        selected ? 'border-ink bg-surface-2' : 'border-line bg-surface hover:bg-surface-4',
      )}
    >
      <input
        type="radio"
        name={name}
        value={value}
        checked={selected}
        onChange={onPick}
        autoComplete="off"
        className="sr-only"
      />
      <div className="flex items-center gap-[9px]">
        <span
          className={clsx(
            'text-[12.5px] leading-none',
            selected ? 'font-semibold text-ink' : 'font-medium text-ink-2',
          )}
        >
          {label}
        </span>
        {selected && (
          <span className="ml-auto size-[6px] flex-none rounded-full bg-success-dot" aria-hidden />
        )}
      </div>
      <span className="text-[10.5px] font-normal leading-[1.45] text-microcopy">{detail}</span>
    </label>
  )
}
